import type { TFunction } from 'i18next';
import { UserRole } from '@/gql/enums';
import type { CompanyRoleSetting } from '@/pages/CompanyManagementPage/shared/Companies.graphql';

/**
 * Returns a sensible default display label for a role when no custom name is configured.
 * Falls back to a spaced PascalCase version of the enum.
 */
export const getDefaultRoleLabel = (role: UserRole): string => {
    switch (role) {
        case UserRole.CompanyAdmin:
            return 'Company Admin';
        case UserRole.CourseAdmin:
            return 'Course Admin';
        case UserRole.Trainer:
            return 'Trainer';
        case UserRole.Trainee:
            return 'Trainee';
        default: {
            const name = String(role);
            return name.replace(/([A-Z])/g, ' $1').trim();
        }
    }
};

/**
 * Build a map of role -> label using provided roleSettings and translation fallbacks.
 * Priority: company customName > i18n key (if present) > default label.
 */
export const buildRoleLabelMap = (
    roleSettings: CompanyRoleSetting[] | undefined,
    t?: TFunction | null
): Record<UserRole, string> => {
    const map = Object.values(UserRole).reduce(
        (acc, role) => {
            const defaultLabel = getDefaultRoleLabel(role);
            const translated = t ? t(`userRoles.${role}`, { defaultValue: defaultLabel }) : defaultLabel;
            acc[role] = translated ?? defaultLabel;
            return acc;
        },
        {} as Record<UserRole, string>
    );

    if (roleSettings && roleSettings.length > 0) {
        for (const setting of roleSettings) {
            if (setting.customName && setting.customName.trim()) {
                map[setting.role] = setting.customName.trim();
            }
        }
    }

    return map;
};

/**
 * Convenience accessor for a single role using a precomputed map.
 */
export const getRoleLabelFromMap = (role: UserRole, labelsMap: Record<UserRole, string>): string => {
    return labelsMap[role] ?? getDefaultRoleLabel(role);
};
